/**
 * Egress-политика исходящих запросов (web/инструкции/скиллы/webhooks/providers/MCP).
 *
 * Единый формат политики (`mode` + `list` + `exceptions`) используется всеми
 * потребителями; `webhooks` дополнительно поддерживает `mode: 'off'` (dev, без
 * проверок и без требования https). Семантика:
 * - `exceptions` — allow-правила, проверяются **первыми** (точный hostname,
 *   `*.suffix`, CIDR/IP); hostname-правило разрешает сразу, CIDR — по резолву;
 * - `deny` + `list` — блок, если хотя бы один resolved-адрес в `list`;
 * - `allow` + `list` — разрешено, если ВСЕ resolved-адреса в `list` (или exception);
 * - `off` (только webhooks) — проверок нет, `requireHttps = false`.
 *
 * Литеральные `localhost`/`*.localhost`/`*.local`/`*.internal` и приватные IP
 * блокируются самой политикой (дефолтный `list`), а не отдельным зашитым списком;
 * exception может их разрешить.
 *
 * Дефолтный `list` — **общий канонический список** `DEFAULT_MCP_PRIVATE_CIDRS`
 * (`types/mcp.ts`): он же — дефолт `mcp.networkPolicy` и встроенный fallback
 * `engine/net/egress-guard.ts`. Списков «заблокированных адресов» в коде больше
 * нет, поэтому расширять блокировки нужно только этот список.
 */
import { DEFAULT_MCP_PRIVATE_CIDRS } from './mcp.js';
export const EGRESS_POLICY_MODES = ['deny', 'allow'];
export const WEBHOOKS_EGRESS_POLICY_MODES = ['deny', 'allow', 'off'];
/** Скоупы настраиваемой egress-политики (settings/egress). */
export const EGRESS_POLICY_SCOPES = ['tools', 'webhooks'];
/**
 * Дефолтная политика встроенных инструментов: deny служебных диапазонов
 * (`DEFAULT_MCP_PRIVATE_CIDRS` — общий канонический список, см. `types/mcp.ts`).
 */
export const DEFAULT_EGRESS_TOOLS_POLICY = {
    mode: 'deny',
    list: [...DEFAULT_MCP_PRIVATE_CIDRS],
    exceptions: [],
};
/**
 * Дефолтная политика webhooks: deny служебных диапазонов (тот же общий список)
 * + обязательный https.
 */
export const DEFAULT_EGRESS_WEBHOOKS_POLICY = {
    mode: 'deny',
    list: [...DEFAULT_MCP_PRIVATE_CIDRS],
    exceptions: [],
};
/** Дефолт журнала внешних запросов: включён, хранить 7 дней. */
export const DEFAULT_EGRESS_LOG = {
    enabled: true,
    retentionDays: 7,
};
/** Скоупы (источники) внешних запросов в журнале egress. */
export const EGRESS_REQUEST_SCOPES = [
    'web_fetch',
    'web_search',
    'skills',
    'instructions',
    'webhooks',
    'providers',
    'mcp',
];
export const EGRESS_REQUEST_STATUSES = ['allowed', 'blocked', 'error'];
/**
 * ADR-099 (D1): фактический маршрут запроса — через прокси, напрямую либо
 * direct-фоллбек после недоступного прокси (`proxy.fallbackDirect`).
 */
export const EGRESS_REQUEST_VIAS = ['proxy', 'direct', 'fallback'];
/** Скоупы изменений egress-политик/прокси в security-аудите. */
export const SECURITY_AUDIT_SCOPES = ['tools', 'webhooks', 'mcp', 'proxy', 'tls', 'attachments', 'context-window', 'context-defaults', 'sticky-notes', 'agents'];
export const SECURITY_AUDIT_ACTIONS = ['update', 'reset'];
